import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@opaz/ui-components';
import { Minus, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';

export const FloatingButtons: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const navigate = useNavigate();
  const currentPath = useRouterState({ select: (state) => state.location.pathname });
  const isChatBotPage = currentPath === '/chat-bot' || currentPath.startsWith('/chat-bot/');
  const analytics = useAnalytics();
  const isMobile = useIsMobile();

  const toggleAccessibility = () => {
    const newState = !accessibilityOpen;
    setAccessibilityOpen(newState);

    // Track accessibility menu toggle
    if (analytics) {
      analytics.trackAccessibilityToggle(newState ? 'open_menu' : 'close_menu');
    }
  };

  const increaseFontSize = () => {
    if (fontSize < 150) {
      const newSize = fontSize + 10;
      setFontSize(newSize);
      document.documentElement.style.fontSize = `${newSize}%`;

      // Track accessibility option change
      if (analytics) {
        analytics.trackAccessibilityOption(`large_text_${newSize}%`);
      }
    }
  };

  const decreaseFontSize = () => {
    if (fontSize > 70) {
      const newSize = fontSize - 10;
      setFontSize(newSize);
      document.documentElement.style.fontSize = `${newSize}%`;

      // Track accessibility option change
      if (analytics) {
        analytics.trackAccessibilityOption(`large_text_${newSize}%`);
      }
    }
  };

  return (
    <TooltipProvider>
      <div>
        <div className="block fixed bottom-20 md:bottom-auto md:top-1/2 md:transform md:-translate-y-1/2 z-[1100] ps-3 md:ps-16 start-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={toggleAccessibility}
                variant="default"
                size="icon"
                className="h-10 w-10 p-0 shadow-lg"
                aria-label={t('floatingButtons.accessibilityOptionsAria')}
              >
                {!accessibilityOpen ? (
                  <img src="/a11y.png" alt={t('floatingButtons.accessibilityButtonAlt')} className="h-15 w-15" />
                ) : (
                  <X className="h-15 w-15" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side={i18n.dir() === 'rtl' ? 'left' : 'right'}>
              <p>{t('floatingButtons.accessibilityOptions')}</p>
            </TooltipContent>
          </Tooltip>

          {/* Accessibility Menu */}
          {accessibilityOpen && (
            <Card className="absolute bottom-full mb-2 md:bottom-auto md:top-0 start-0 md:start-32 shadow-lg rounded-lg w-[calc(100vw-1.5rem)] max-w-72 md:w-72">
              <CardHeader className="py-3">
                <CardTitle className="text-lg text-center">{t('floatingButtons.accessibilityCardTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center gap-2">
                      <Button
                        onClick={decreaseFontSize}
                        variant="ghost"
                        size="icon"
                        className="flex-1"
                        aria-label="Decrease font size"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <p className="text-sm whitespace-nowrap">{t('floatingButtons.fontSizeLabel', { fontSize })}</p>
                      <Button
                        onClick={increaseFontSize}
                        variant="ghost"
                        size="icon"
                        className="flex-1"
                        aria-label="Increase font size"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {!isChatBotPage && (
          <div className="block fixed bottom-20 md:bottom-auto md:top-1/2 md:transform md:-translate-y-1/2 z-[1100] pe-3 md:pe-16 end-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => {
                    // Track chatbot engagement
                    if (analytics) {
                      analytics.trackChatbotEngaged('floating_button', isMobile ? 'mobile' : 'desktop');
                    }
                    navigate({
                      to: '/chat-bot',
                      search: { entryPoint: 'floating_button' },
                    });
                  }}
                  variant="default"
                  size="icon"
                  className="h-12 w-12 p-0 shadow-lg"
                  aria-label={t('chatbot.chatWithUs')}
                >
                  <img src="/chatbot.svg" alt={t('chatbot.chatWithUs')} className="h-12 w-12" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side={i18n.dir() === 'rtl' ? 'right' : 'left'}>
                <p>{t('chatbot.chatWithUs')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
